import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

import {
  utcEndOfDay,
  utcStartOfDay,
} from 'components/date-range-picker/utc-date-utils'

dayjs.extend(utc)

// 2018-01-01 default yogi start date (UTC midnight, matching the UTC convention
// the rest of the picker stores in — `new Date(2018, 0, 1)` is local midnight).
export const ALL_TIME_START_DATE = new Date(Date.UTC(2018, 0, 1))

export const DEFAULT_DATE_RANGE_MONTHS = 12

export const getDefaultDateRange = (): { startDate: Date; endDate: Date } => {
  const now = dayjs.utc()
  return {
    startDate: utcStartOfDay(
      now.subtract(DEFAULT_DATE_RANGE_MONTHS, 'month'),
    ).toDate(),
    endDate: utcEndOfDay(now).toDate(),
  }
}

// Map months to shorthand keys for UI (used by DateRangeSelector)
export const DEFAULT_DATE_SHORTHAND = '12m' as const

// Date selector options
export const SHORTHAND_DATE_OPTIONS = [
  { key: '7d', label: 'Last 7 days' },
  { key: '30d', label: 'Last 30 days' },
  { key: '3m', label: 'Last 3 months' },
  { key: '6m', label: 'Last 6 months' },
  { key: '12m', label: 'Last 12 months' },
  { key: '2y', label: 'Last 2 years' },
  { key: 'all', label: 'All time' },
] as const

export const XTD_DATE_OPTIONS = [
  { key: 'wtd', label: 'Week to Date' },
  { key: 'mtd', label: 'Month to Date' },
  { key: 'qtd', label: 'Quarter to Date' },
  { key: 'ytd', label: 'Year to Date' },
] as const

// The "no known preset" tail of the label: Today / Yesterday / "Since X" /
// an explicit "X - Y" range. Shared by formatDateRangeLabel below and by
// DateRangeSelector's own getDisplayText, so the two never drift.
const formatCustomDateRangeLabel = (startDate: Date, endDate: Date): string => {
  const start = dayjs(startDate).utc()
  const end = dayjs(endDate).utc()
  const now = dayjs.utc()

  if (start.isSame(now, 'day') && end.isSame(now, 'day')) return 'Today'

  const yesterday = now.subtract(1, 'day')
  if (start.isSame(yesterday, 'day') && end.isSame(yesterday, 'day')) {
    return 'Yesterday'
  }

  if (end.isSame(now, 'day')) return `Since ${start.format('MMM D, YYYY')}`

  return `${start.format('MMM D, YYYY')} - ${end.format('MMM D, YYYY')}`
}

// The picker's "as-shown" label for a resolved range: a preset's own label
// when `source`/`presetKey` name one, otherwise the explicit-dates fallback.
// Exported so consumers that only get the `onDateRangeChange` callback args
// (e.g. ask-yogi's TimeTokenSelect) can format the same label
// DateRangeSelector's button shows, without re-deriving the logic.
export const formatDateRangeLabel = (
  startDate: Date,
  endDate: Date,
  source?: 'shorthand' | 'xtd' | 'custom' | null,
  presetKey?: string | null,
): string => {
  if (source === 'shorthand' && presetKey) {
    const option = SHORTHAND_DATE_OPTIONS.find((o) => o.key === presetKey)
    if (option) return option.label
  }
  if (source === 'xtd' && presetKey) {
    const option = XTD_DATE_OPTIONS.find((o) => o.key === presetKey)
    if (option) return option.label
  }
  return formatCustomDateRangeLabel(startDate, endDate)
}
